import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Building2, TrendingUp, Users, DollarSign, MapPin, Briefcase } from "lucide-react";
import SeoHead from "@/components/shared/SeoHead";
import PageHero, { HeroAccent, HeroStatTile } from "@/components/shared/PageHero";
import { AdSense, LeaderboardAd, InContentAd, SidebarAd } from "@/components/shared/AdSense";
import AdFrame from "@/components/shared/AdFrame";
import SalaryTable from "@/components/shared/SalaryTable";
import RppToggle from "@/components/shared/RppToggle";
import AffiliateCta from "@/components/shared/AffiliateCta";
import { supabase } from "@/integrations/supabase/client";
import IndustrySelector from "@/components/selectors/IndustrySelector";
import { formatLocationName } from "@/lib/utils";

const IndustryCenter = () => {
  const { naics, location } = useParams<{ naics: string; location: string }>();
  const navigate = useNavigate();
  const [useRpp, setUseRpp] = useState(false);

  const naicsCode = naics ?? '';
  const locationName = formatLocationName(location || '');
  const showSelector = !naics || !location;

  // Industry mapping for SEO and display
  const industryMap: Record<string, { title: string; description: string }> = {
    '11': { title: 'Agriculture, Forestry, Fishing and Hunting', description: 'Primary agricultural production and related services' },
    '21': { title: 'Mining, Quarrying, Oil and Gas Extraction', description: 'Extraction of natural resources including oil, gas, and minerals' },
    '22': { title: 'Utilities', description: 'Electric power, natural gas, water, and waste management services' },
    '23': { title: 'Construction', description: 'Building construction, infrastructure, and specialty trade contractors' },
    '31': { title: 'Manufacturing', description: 'Production of goods from raw materials and components' },
    '42': { title: 'Wholesale Trade', description: 'Distribution of goods to retailers, businesses, and institutions' },
    '44': { title: 'Retail Trade', description: 'Direct sales of goods to consumers through stores and online' },
    '48': { title: 'Transportation and Warehousing', description: 'Moving people and goods, storage and logistics services' },
    '51': { title: 'Information', description: 'Publishing, broadcasting, telecommunications, and data processing' },
    '52': { title: 'Finance and Insurance', description: 'Banking, investment, insurance, and financial services' },
    '53': { title: 'Real Estate and Rental', description: 'Property development, management, and equipment rental' },
    '54': { title: 'Professional, Scientific, and Technical Services', description: 'Specialized professional services and consulting' },
    '55': { title: 'Management of Companies', description: 'Corporate headquarters and holding companies' },
    '56': { title: 'Administrative and Support Services', description: 'Business support services and facilities management' },
    '61': { title: 'Educational Services', description: 'Schools, colleges, universities, and training institutions' },
    '62': { title: 'Health Care and Social Assistance', description: 'Medical services, hospitals, and social care programs' },
    '71': { title: 'Arts, Entertainment, and Recreation', description: 'Entertainment venues, sports, arts, and recreational facilities' },
    '72': { title: 'Accommodation and Food Services', description: 'Hotels, restaurants, and food service establishments' },
    '81': { title: 'Other Services', description: 'Personal services, repairs, and miscellaneous professional services' },
    '92': { title: 'Public Administration', description: 'Government agencies and public sector organizations' }
  };

  const currentIndustry = industryMap[naicsCode] || { 
    title: 'Unknown Industry', 
    description: 'Industry information not available' 
  };

  // Get area code for database query
  const getAreaCode = (location: string): string => {
    const areaMappings: Record<string, string> = {
      'united states': '99',
      'alabama': '01', 'alaska': '02', 'arizona': '04', 'arkansas': '05', 'california': '06',
      'colorado': '08', 'connecticut': '09', 'delaware': '10', 'florida': '12', 'georgia': '13',
      'hawaii': '15', 'idaho': '16', 'illinois': '17', 'indiana': '18', 'iowa': '19',
      'kansas': '20', 'kentucky': '21', 'louisiana': '22', 'maine': '23', 'maryland': '24',
      'massachusetts': '25', 'michigan': '26', 'minnesota': '27', 'mississippi': '28', 'missouri': '29',
      'montana': '30', 'nebraska': '31', 'nevada': '32', 'new hampshire': '33', 'new jersey': '34',
      'new mexico': '35', 'new york': '36', 'north carolina': '37', 'north dakota': '38', 'ohio': '39',
      'oklahoma': '40', 'oregon': '41', 'pennsylvania': '42', 'rhode island': '44', 'south carolina': '45',
      'south dakota': '46', 'tennessee': '47', 'texas': '48', 'utah': '49', 'vermont': '50',
      'virginia': '51', 'washington': '53', 'west virginia': '54', 'wisconsin': '55', 'wyoming': '56'
    };
    return areaMappings[location.toLowerCase()] || '99';
  };

  const areaCode = getAreaCode(locationName);

  // Fetch industry occupation data
  const { data: industryData, isLoading } = useQuery({
    // useRpp intentionally not in the key: the toggle is disabled here
    // (industry data is national-only, RPP baseline = 100).
    queryKey: ['industry-occupations', naicsCode, areaCode],
    queryFn: async () => {
      // Use Edge Function instead of direct database query
      const { data, error } = await supabase.functions.invoke('api-occupations', {
        body: {
          naics: naicsCode,
          area: areaCode,
          limit: 20
        }
      });
      
      if (error) throw error;
      return data?.occupations || [];
    },
    enabled: !showSelector && Boolean(naicsCode) && Boolean(locationName)
  });

  if (showSelector) {
    return <IndustrySelector />;
  }

  // Calculate industry summary stats
  const industrySummary = industryData ? {
    totalJobs: industryData.reduce((sum, occ) => sum + (occ.employment || 0), 0),
    avgSalary: Math.round(industryData.reduce((sum, occ) => sum + (occ.median_salary || 0), 0) / industryData.length),
    topPaying: Math.max(...industryData.map(occ => occ.median_salary || 0)),
    occupationCount: industryData.length
  } : null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const pageTitle = `${currentIndustry.title} Jobs & Salaries in ${locationName} - Industry Career Guide`;
  const pageDescription = `${currentIndustry.title} careers in ${locationName}: ${formatCurrency(industrySummary?.avgSalary || 75000)} average salary, ${formatNumber(industrySummary?.totalJobs || 50000)} total jobs. Explore top occupations and career paths.`;

  // SeoHead must render even while data is loading: prerender validation
  // rejects any snapshot whose <title> is still the default SPA shell.
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background pt-16">
        <SeoHead title={pageTitle} description={pageDescription} />
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 space-y-6">
              <div className="h-8 bg-muted animate-pulse rounded" />
              <div className="h-32 bg-muted animate-pulse rounded" />
              <div className="h-96 bg-muted animate-pulse rounded" />
            </div>
            <div className="space-y-4">
              <div className="h-64 bg-muted animate-pulse rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={pageTitle}
        description={pageDescription}
      />

      {/* Hero Section */}
      <PageHero
        badge={{ label: `NAICS ${naicsCode} - Industry Analysis` }}
        title={
          <>
            {currentIndustry.title} in
            <HeroAccent> {locationName}</HeroAccent>
          </>
        }
        subtitle={currentIndustry.description}
      >
        {/* Top ad placement */}
        <AdFrame className="flex justify-center mb-8">
          <LeaderboardAd className="max-w-4xl" />
        </AdFrame>

        {industrySummary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <HeroStatTile icon={DollarSign} value={formatCurrency(industrySummary.avgSalary)} label="Average Salary" />
            <HeroStatTile icon={Users} value={formatNumber(industrySummary.totalJobs)} label="Total Jobs" />
            <HeroStatTile icon={TrendingUp} value={formatCurrency(industrySummary.topPaying)} label="Top Salary" />
            <HeroStatTile icon={Briefcase} value={`${industrySummary.occupationCount}+`} label="Occupations" />
          </div>
        )}
      </PageHero>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content Area */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* Industry Overview */}
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle className="text-xl font-semibold">
                    Top Occupations in {currentIndustry.title}
                  </CardTitle>
                  <RppToggle
                    enabled={useRpp}
                    onToggle={setUseRpp}
                    disabled
                    disabledReason="BLS publishes industry wage detail nationally only, and the United States is the RPP baseline (100) — real and nominal wages match here."
                  />
                </div>
              </CardHeader>
              <CardContent>
                {industryData && industryData.length > 0 ? (
                  <SalaryTable
                    data={industryData.map(item => ({
                      occ_title: item.occ_title || 'Unknown Occupation',
                      soc: item.soc_code || '',
                      annual_mean_wage: item.median_salary || 0,
                      annual_median_wage: item.median_salary || 0,
                      hourly_mean_wage: Math.round((item.median_salary || 0) / 2080) || 0,
                      total_employment: item.employment || 0
                    }))}
                    showAffiliateEvery={5}
                    area={locationName}
                    className="shadow-none border-0"
                  />
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No occupation data available for this industry in {locationName}.
                  </div>
                )}

                {/* In-content ad */}
                <InContentAd />

                {/* Industry career advancement */}
                <div className="mt-8 p-6 bg-gradient-to-r from-accent/10 to-accent/5 rounded-lg border border-accent/20">
                  <div className="flex items-start gap-4">
                    <Building2 className="h-6 w-6 text-accent mt-1" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2">Break Into {currentIndustry.title}</h3>
                      <p className="text-muted-foreground mb-4">
                        Ready to start or advance your career in this industry? Find specialized training 
                        programs and job opportunities in {locationName}.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <AffiliateCta
                          provider="ziprecruiter"
                          occupation={currentIndustry.title}
                          location={locationName}
                          type="job-search"
                          className="bg-accent hover:bg-accent/90"
                        />
                        <AffiliateCta
                          provider="indeed"
                          occupation={currentIndustry.title}
                          location={locationName}
                          type="job-search"
                          className="bg-accent hover:bg-accent/90"
                        />
                        <AffiliateCta
                          provider="coursera"
                          occupation={currentIndustry.title}
                          location={locationName}
                          type="training"
                          className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                        />
                        <AffiliateCta
                          provider="linkedin-learning"
                          occupation={currentIndustry.title}
                          location={locationName}
                          type="training"
                          className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Industry insights */}
            <Card>
              <CardHeader>
                <CardTitle>Industry Insights & Career Paths</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm prose-neutral dark:prose-invert max-w-none">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Growth Trends</h4>
                    <p className="text-muted-foreground mb-4">
                      The {currentIndustry.title.toLowerCase()} sector in {locationName} offers diverse 
                      career opportunities across multiple skill levels and specializations.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                        <span>Strong demand for specialized skills</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                        <span>Remote work opportunities available</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                        <span>Multiple career advancement paths</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-wage-accent">Entry Requirements</h4>
                    <p className="text-muted-foreground mb-4">
                      Entry into {currentIndustry.title.toLowerCase()} varies by role, with opportunities 
                      for both entry-level and experienced professionals.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-wage-accent rounded-full mt-2"></div>
                        <span>Education requirements vary by position</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-wage-accent rounded-full mt-2"></div>
                        <span>Professional certifications valued</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-wage-accent rounded-full mt-2"></div>
                        <span>Experience often prioritized over credentials</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Sidebar Ad */}
            <SidebarAd />

            {/* Related Industries */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Related Industries</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(industryMap).slice(0, 6).map(([code, industry]) => (
                  <Button 
                    key={code}
                    variant={code === naicsCode ? "default" : "outline"} 
                    className="w-full justify-start text-sm"
                    onClick={() => navigate(`/industry/${code}/${location}`)}
                  >
                    {industry.title}
                  </Button>
                ))}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Industry Tools</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate(`/calculators`)}
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Salary Calculator
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate(`/no-degree-jobs/${location}`)}
                >
                  <Briefcase className="h-4 w-4 mr-2" />
                  No-Degree Jobs
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate(`/`)}
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  Other Locations
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustryCenter;